import { ethers } from "ethers";
import config from "../config/index.js";

const POLYGON_AMOY_CHAIN_ID = 80002;
const AMOY_MIN_PRIORITY_FEE = ethers.parseUnits("30", "gwei");
const AMOY_DEFAULT_MAX_FEE = ethers.parseUnits("60", "gwei");

/**
 * Returns transaction overrides appropriate for the configured network.
 *
 * Polygon Amoy nodes reject any transaction whose tip cap is below 25 gwei.
 * MetaMask's automatic estimate frequently lands at 1.5 gwei which means
 * every write would otherwise fail with "transaction gas price below
 * minimum". We force a 30 gwei priority fee and a 60 gwei max fee on Amoy,
 * topping up further if the provider's own fee data is higher.
 *
 * @param {import("ethers").AbstractProvider} provider
 * @returns {Promise<object>} overrides usable as the last arg of a contract call
 */
export async function getTxOverrides(provider) {
    if (config.network.chainId !== POLYGON_AMOY_CHAIN_ID) return {};

    let priority = AMOY_MIN_PRIORITY_FEE;
    let max = AMOY_DEFAULT_MAX_FEE;
    try {
        const feeData = await provider.getFeeData();
        if (feeData.maxPriorityFeePerGas && feeData.maxPriorityFeePerGas > priority) {
            priority = feeData.maxPriorityFeePerGas;
        }
        if (feeData.maxFeePerGas && feeData.maxFeePerGas > max) {
            max = feeData.maxFeePerGas;
        }
    } catch {
        // fall through to defaults
    }
    if (max < priority * 2n) max = priority * 2n;
    return { maxPriorityFeePerGas: priority, maxFeePerGas: max };
}
